// @flow

import React from 'react';
import styled from 'styled-components';
import { colors, fonts } from 'shared/styleguide';
import { i18n } from 'shared/utils';

type Props = {
  currentPage: number,
  totalCount: number,
  pageSize: number,
  onPageChange: (page: number) => void,
};

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  margin-top: 8px;
`;

const PageInfo = styled.span`
  ${fonts.smallRegular};
  color: ${colors.battleshipGrey};
`;

const PageButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const PageButton = styled.button`
  min-width: 32px;
  height: 32px;
  padding: 0 8px;
  border: 1px solid ${p => (p.active ? colors.azul : colors.lightBlueGrey)};
  border-radius: 6px;
  background-color: ${p => (p.active ? colors.azul : colors.white)};
  color: ${p => (p.active ? colors.white : colors.charcoalGrey)};
  ${fonts.smallRegular};
  cursor: ${p => (p.disabled ? 'default' : 'pointer')};
  opacity: ${p => (p.disabled ? 0.4 : 1)};
  outline: none;

  &:hover:not(:disabled) {
    background-color: ${p => (p.active ? colors.azul : colors.paleGrey)};
  }
`;

const Ellipsis = styled.span`
  min-width: 24px;
  text-align: center;
  color: ${colors.battleshipGrey};
  ${fonts.smallRegular};
`;

function getPageNumbers(currentPage: number, totalPages: number): Array<number | string> {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const pages = [];
  pages.push(1);

  if (currentPage > 3) {
    pages.push('...');
  }

  const start = Math.max(2, currentPage - 1);
  const end = Math.min(totalPages - 1, currentPage + 1);

  for (let i = start; i <= end; i++) {
    pages.push(i);
  }

  if (currentPage < totalPages - 2) {
    pages.push('...');
  }

  pages.push(totalPages);

  return pages;
}

const PaginationControls = ({
  currentPage,
  totalCount,
  pageSize,
  onPageChange,
}: Props) => {
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalCount);

  if (totalCount <= pageSize) {
    return null;
  }

  const pages = getPageNumbers(currentPage, totalPages);

  return (
    <Wrapper>
      <PageInfo>
        {i18n.t('Showing {{startItem}}-{{endItem}} of {{totalCount}}', {
          startItem,
          endItem,
          totalCount,
        })}
      </PageInfo>
      <PageButtons>
        <PageButton
          disabled={currentPage <= 1}
          onClick={() => onPageChange(currentPage - 1)}
        >
          ‹
        </PageButton>
        {pages.map((page, idx) =>
          page === '...' ? (
            <Ellipsis key={`ellipsis-${idx}`}>…</Ellipsis>
          ) : (
            <PageButton
              key={page}
              active={page === currentPage}
              onClick={() => onPageChange(page)}
            >
              {page}
            </PageButton>
          ),
        )}
        <PageButton
          disabled={currentPage >= totalPages}
          onClick={() => onPageChange(currentPage + 1)}
        >
          ›
        </PageButton>
      </PageButtons>
    </Wrapper>
  );
};

export default PaginationControls;
